import { Flex } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { Alert, Button, Card, Drawer, List, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';

import { NtsCertificate, SCRAPE_STATUS } from '~apis/scrape';
import CertificateComponent from '~components/DataManage/Onboarding/Certificate';
import { css } from '~libs/emotion';

export const getAnalyzeStatusTag = (status: SCRAPE_STATUS) => {
  switch (status) {
    case 'SUCCESS':
      return <Tag color="success">수집 완료</Tag>;
    case 'ERROR':
    case 'FATAL':
      return <Tag>수집 실패</Tag>;
    case 'IN_PROGRESS':
      return <Tag color="processing">분석중</Tag>;
    default:
      return <div />;
  }
};

interface ListForHometaxProps {
  title: string;
  dataSource: HomeTaxEntity[];
  certificates: NtsCertificate[];
}

const MAP_TYPE = {
  SALES: '매출',
  PURCHASE: '매입',
};

interface HomeTaxEntity {
  type: HomeTaxEntityType;
  scrapedAt: string;
  status: SCRAPE_STATUS;
}

export type HomeTaxEntityType =
  | 'TAX_INVOICE_SALES'
  | 'TAX_INVOICE_PURCHASE'
  | 'CASH_RECEIPT_SALES'
  | 'CASH_RECEIPT_PURCHASE';

const getItemTitle = (type: HomeTaxEntityType) => {
  switch (type) {
    case 'TAX_INVOICE_SALES':
      return '세금계산서 매출';
    case 'TAX_INVOICE_PURCHASE':
      return '세금계산서 매입';
    case 'CASH_RECEIPT_SALES':
      return '현금영수증 매출';
    case 'CASH_RECEIPT_PURCHASE':
      return '현금영수증 매입';
  }
};

export const ListForHometax: React.FC<ListForHometaxProps> = ({
  title,
  dataSource,
  certificates,
}) => {
  const disconnected = certificates.length == 0 || certificates.every((x) => x.status !== 'NORMAL');
  return (
    <Card title={title} bordered={false}>
      <AnimatePresence mode="popLayout">
        {disconnected && (
          <motion.div
            key="abnormal-accounts"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            className={css`
              padding-bottom: 16px;
            `}
          >
            <Alert
              message="홈택스 연결 수단이 없습니다"
              description={<>홈택스에 로그인 가능한 공동인증서를 다시 등록해주세요</>}
              type="error"
              showIcon
              banner
              action={
                <Flex gap={4} align="center">
                  <Button
                    size="small"
                    onClick={async () => {
                      overlays.open(({ isOpen, onClose }) => (
                        <Drawer
                          open={isOpen}
                          onClose={onClose}
                          width={1000}
                          destroyOnClose
                          title="공동인증서 등록"
                        >
                          <CertificateComponent />
                        </Drawer>
                      ));
                    }}
                  >
                    공동인증서 관리
                  </Button>
                </Flex>
              }
            />
          </motion.div>
        )}
      </AnimatePresence>
      <List
        itemLayout="horizontal"
        dataSource={dataSource}
        renderItem={(item) => (
          <List.Item>
            <Flex justify="space-between" align="center" style={{ width: '100%' }}>
              <Flex align="center">
                <Typography.Text>{getItemTitle(item.type)}</Typography.Text>
              </Flex>
              <Flex align="center" gap={12}>
                <Typography.Text>
                  {item.scrapedAt ? dayjs(item.scrapedAt).format('YYYY-MM-DD HH:mm:ss') : ''}
                </Typography.Text>
                {getAnalyzeStatusTag(item.status)}
              </Flex>
            </Flex>
          </List.Item>
        )}
      />
    </Card>
  );
};
